<template>
  <q-footer
    elevated
    class="bg-grey-2 text-black "
  >
    <div
      v-if="!hideSessionPrograms"
      class="bg-grey-2 text-grey-6 cursor-pointer progress-drag-handle z-top"
      @click="onToggleSessionProgress"
    >
      <q-icon name="drag_handle" />
    </div>
    <q-toolbar
      class="q-pl-none q-pr-none z-top pointer-events-none"
    >
      <div
        style="height:auto; width:100%"
        class="bg-grey-2"
      >
        <div
          class="full-width row items-center"
          style="height:auto"
        >
          <div class="col q-pt-sm">
            <p
              v-if="session"
              class="q-ma-none q-pt-none q-pb-none q-pl-md q-pr-md ellipsis text-subtitle2"
            >
              {{ session.sessionName }}
            </p>
          </div>
          <div
            class="col-auto row justify-end items-center q-col-gutter-sm q-pt-sm q-pb-xs q-pr-md"
          >
            <div>
              <q-btn-dropdown
                v-if="canFinishSession"
                split
                color="secondary"
                icon="done"
                :label="finishSessionLabel"
                size="sm"
                class="pointer-events-auto q-mr-md"
                data-e2e="button-finish-session"
                @click="onFinishSession"
              >
                <q-list>
                  <q-item
                    v-close-popup
                    clickable
                    dense
                    data-e2e="button-exit-to-main"
                    @click="onExitSession"
                  >
                    <q-item-section avatar>
                      <q-avatar
                        icon="home"
                        size="md"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>
                        {{ $t('sessions.sessionProgress.itemExitToMainScreenLabel') }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
              <q-btn
                v-else
                color="grey-3"
                text-color="black"
                icon="home"
                :label="$q.screen.gt.xs
                  ? $t('sessions.sessionProgress.btnExitToMainScreenLabel')
                  : ''"
                size="sm"
                class="pointer-events-auto q-mr-md"
                @click="onExitSession"
              />
            </div>
            <div
              v-if="!hideProgramNavigation"
            >
              <q-btn
                icon="skip_previous"
                color="grey-3"
                text-color="black"
                :disable="isFirstProgram"
                size="sm"
                class="pointer-events-auto"
                @click="onPreviousProgram"
              />
            </div>
            <div
              v-if="!hideProgramNavigation"
            >
              <q-btn
                color="primary"
                :disable="isLastProgram"
                icon="skip_next"
                size="sm"
                :label="$q.screen.gt.xs ? $t('run.collectData.buttonNextProgram') : ''"
                class="pointer-events-auto"
                @click="onNextProgram"
              />
            </div>
            <div
              v-if="!hideFullScreenSwitch"
            >
              <q-btn
                v-if="hideSessionPrograms"
                flat
                round
                dense
                size="sm"
                icon="fullscreen_exit"
                class="pointer-events-auto"
                @click="onContractView"
              >
                <q-tooltip>
                  {{ $t('run.collectData.tooltipExitExpanded') }}
                </q-tooltip>
              </q-btn>
              <q-btn
                v-else
                flat
                round
                dense
                size="sm"
                icon="fullscreen"
                class="pointer-events-auto"
                @click="onExpandView"
              >
                <q-tooltip>
                  {{ $t('sessions.sessionProgress.tooltipExpandToShow') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div
              v-if="!hideFilter && (hasProgramTags || hasFilter)"
            >
              <q-btn
                flat
                round
                dense
                size="sm"
                icon="filter_list"
                :color="hasFilter ? 'primary' : 'dark'"
                class="pointer-events-auto"
                @click="onFilter"
              >
                <q-tooltip>
                  {{ $t('run.collectData.filterTitle') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div v-if="canParticipate || canStopParticipation || !hideRefresh">
              <q-btn
                flat
                round
                dense
                size="sm"
                icon="more_vert"
                class="pointer-events-auto"
              >
                <q-menu>
                  <q-list>
                    <q-item
                      v-if="canParticipate"
                      v-ripple
                      v-close-popup
                      clickable
                      class="text-no-wrap"
                      @click="onParticipate"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="group_add"
                          color="dark"
                        />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionProgress.itemParticipateLabel') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="canStopParticipation"
                      v-ripple
                      v-close-popup
                      clickable
                      class="text-no-wrap"
                      @click="onStopParticipation"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="group_remove"
                          color="dark"
                        />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionProgress.itemStopParticipationLabel') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="canParticipate"
                      v-ripple
                      v-close-popup
                      clickable
                      class="text-no-wrap"
                      @click="onRequestSessionControl"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="next_plan"
                          color="dark"
                        />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionProgress.itemRequestSessionControlLabel') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="!hideRefresh"
                      v-ripple
                      v-close-popup
                      clickable
                      class="text-no-wrap"
                      @click="onRefreshProgramsAndTargets"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="refresh"
                          color="dark"
                        />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionProgress.itemonRefreshProgramsAndTargetsLabel') }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </div>
        </div>
        <div
          v-if="!hideSessionPrograms"
          class="full-width q-pt-none q-mt-none pointer-events-auto"
        >
          <q-scroll-area
            ref="sessionBuilderScroll"
            class="bg-grey-2 session-builder-inner"
            :class="{ minimized }"
            style="width:100%;"
          >
            <div class="row no-wrap q-ma-xs">
              <session-program-card
                v-for="sessionProgram in sessionPrograms"
                ref="sessionProgramEl"
                :key="sessionProgram.id"
                :session-program="sessionProgram"
                :is-current="currentSessionProgram === sessionProgram"
                hide-menu
                @change-target-status-selected="onChangeTargetStatusSelected"
                @click="onGoToSessionProgram(sessionProgram)"
              />
            </div>
          </q-scroll-area>
        </div>
      </div>
    </q-toolbar>
  </q-footer>
</template>
<script>
import SessionProgramCard from 'components/Sessions/SessionProgramCard';
import { chain, get, isEmpty, size } from 'lodash';
import { mapState } from 'pinia';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';

export default {
  name: 'SessionProgress',

  components: { SessionProgramCard },

  mixins: [Interactions],

  props: {
    sessionPrograms: {
      type: Array,
      required: true,
    },
    currentSessionProgram: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    canParticipate: {
      type: Boolean,
      required: false,
    },
    canStopParticipation: {
      type: Boolean,
      required: false,
    },
    isCurrentSessionOwner: {
      type: Boolean,
      required: true,
    },
    hideSessionPrograms: {
      type: Boolean,
      required: true,
    },
    hideProgramNavigation: {
      type: Boolean,
      default: false,
    },
    hideFullScreenSwitch: {
      type: Boolean,
      default: false,
    },
    hideFilter: {
      type: Boolean,
      default: false,
    },
    hideRefresh: {
      type: Boolean,
      default: false,
    },
    isActiveSessionProgress: {
      type: Boolean,
      default: true,
    },
    finishLabel: {
      type: String,
      default: null,
    },
    filteredTags: {
      type: Array,
      default: () => ([]),
    },
  },

  emits: [
    'finish-session',
    'exit-session',
    'participate',
    'stop-participation',
    'request-session-control',
    'refresh-programs',
    'expand',
    'contract',
    'filter',
    'goto',
    'change-target-status-selected',
  ],

  data() {
    return {
      minimized: true,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),

    isNativeIos() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.ios;
    }, // isNativeIos()

    canFinishSession() {
      const isTrainingSession = get(this.session, 'sessionType') === 'training';
      const isEmptyTrainingSession = isTrainingSession && this.numberOfPrograms === 0;
      const isEmptyLibrarySession = isEmptyTrainingSession && this.session?.packageInheritedId;

      if (isEmptyLibrarySession) {
        return false;
      }

      if (isEmptyTrainingSession) {
        return true;
      }

      // If it's a training session and it's not empty, we can't Finish it with the Finish Session button
      if (isTrainingSession) {
        return false;
      }

      return this.isCurrentSessionOwner;
    }, // canFinishSession()

    numberOfPrograms() {
      return size(this.sessionPrograms);
    }, // numberOfProgram()

    hasProgramTags() {
      return !chain(this.sessionPrograms)
        .map('program.tags')
        .flatten()
        .compact()
        .isEmpty()
        .value();
    }, // hasProgramTags()

    currentProgramIndex() {
      return this.sessionPrograms.indexOf(this.currentSessionProgram) + 1;
    }, // currentProgramIndex()

    isFirstProgram() {
      return this.currentProgramIndex === 1;
    }, // isFirstProgram()

    isLastProgram() {
      return this.currentProgramIndex === this.sessionPrograms.length;
    }, // isLastProgram()

    hasFilter() {
      return !isEmpty(this.filteredTags);
    },

    finishSessionLabel() {
      return this.$q.screen.gt.xs
        ? (this.finishLabel || this.$t('run.collectData.buttonFinishSession'))
        : '';
    },
  },

  watch: {
    currentSessionProgram: {
      deep: false,
      immediate: true,
      handler() {
        if (!!this.$refs.sessionProgramEl
          && this.currentProgramIndex
          && (this.currentProgramIndex in this.$refs.sessionProgramEl)) {
          const scrollDest = this.$refs.sessionProgramEl[this.currentProgramIndex - 1].$el
            .offsetLeft - 150;
          this.$refs.sessionBuilderScroll.setScrollPosition('horizontal', scrollDest, 500);
        }
      },
    },
  },

  methods: {
    onFinishSession() {
      this.$emit('finish-session');
    }, // onFinishSession()

    onExitSession() {
      this.$emit('exit-session');
    }, // onExitSession()

    onPreviousProgram() {
      if (this.isFirstProgram) {
        return;
      }
      const sessionProgram = get(this.sessionPrograms, this.currentProgramIndex - 2, null);
      if (sessionProgram !== null) {
        this.onGoToSessionProgram(sessionProgram);
      }
    }, // onPreviousProgram()

    onNextProgram() {
      if (this.isLastProgram) {
        return;
      }
      const sessionProgram = get(this.sessionPrograms, this.currentProgramIndex, null);

      if (sessionProgram !== null) {
        this.onGoToSessionProgram(sessionProgram);
      }
    }, // onNextProgram()

    onParticipate() {
      this.$emit('participate');
    },

    onStopParticipation() {
      this.$emit('stop-participation');
    },

    onRequestSessionControl() {
      this.$emit('request-session-control');
    }, // onRequestSessionControl()

    onRefreshProgramsAndTargets() {
      this.$emit('refresh-programs');
    }, // onRefreshProgramsAndTargets()

    onExpandView() {
      this.$emit('expand');
    }, // onExpandView()

    onContractView() {
      this.$emit('contract');
    }, // onContractView()

    onFilter() {
      this.$emit('filter');
    },

    onGoToSessionProgram(sessionProgram) {
      if (this.currentSessionProgram.id !== sessionProgram.id) {
        this.$emit('goto', sessionProgram);
      }
    }, // onGoToSessionProgram()

    onChangeTargetStatusSelected(targetStatusInfo) {
      this.$emit('change-target-status-selected', targetStatusInfo);
    }, // onChangeTargetStatusSelected()

    onToggleSessionProgress() {
      if (!this.isNativeIos && !this.isActiveSessionProgress) {
        return;
      }

      this.minimized = !this.minimized;
    }, // onToggleSessionProgress()
  },
};
</script>
<style lang="scss" scoped>
.progress-drag-handle {
  position: absolute;
  top: -18px;
  width: 40px;
  text-align: center;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 8px 8px 0px 0px;
  box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.2), 0 0px 10px rgba(0, 0, 0, 0.24);
}

.session-builder-inner.minimized {
  height: 16px;
}

.session-builder-inner {
  height: 30vh;
  transition: height .2s ease-in-out;

  &.minimized {
    height: 16px;
  }
}
.pointer-events-none {
  pointer-events: none;
}
.pointer-events-auto {
  pointer-events: auto;
}
</style>
